import { invoke } from "@tauri-apps/api/core";

export type Priority = "high" | "medium" | "low";
export type FilterMode = "today" | "active" | "all" | "completed" | "high" | "trashed";

export interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  priority: Priority;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  notionPageId: string | null;
  notionLastEditedTime: string | null;
  deleted: boolean;
  projectId: string | null;
  repeatRule: string | null;
  repeatKey: string | null;
  owner: string;
  goalId: string | null;
}

export interface TaskStats {
  active: number;
  completed: number;
  today: number;
  high: number;
  total: number;
}

export type ProjectKind = "work" | "life";

export interface Project {
  id: string;
  name: string;
  kind: ProjectKind;
  color: string;
  archived: boolean;
  createdAt: string;
}

export interface CreateProjectInput {
  name: string;
  kind?: ProjectKind;
  color?: string;
}

export interface UpdateProjectPatch {
  name?: string;
  kind?: ProjectKind;
  color?: string;
  archived?: boolean;
}

export interface KpiEntry {
  id: string;
  projectId: string;
  date: string;
  key: string;
  value: string;
  updatedAt: string;
}

export interface UpsertKpiInput {
  projectId: string;
  key: string;
  value: string;
  date?: string;
}

export interface InboxSuggestion {
  taskId: string;
  projectId: string | null;
  owner: string;
  confidence: number;
}

export interface ProjectBrief {
  id: string;
  name: string;
  kind: ProjectKind;
}

export interface Settings {
  theme: "system" | "light" | "dark";
  syncIntervalSec: number;
  notionParentPageId: string | null;
  notionDatabaseId: string | null;
  notionDataSourceId: string | null;
  hasNotionToken: boolean;
}

export interface IntegrationStatus {
  hasToken: boolean;
  apiPort: number | null;
}

export interface GcalStatus {
  connected: boolean;
  email: string | null;
  hasClientConfig: boolean;
}

export interface GcalEventItem {
  id: string;
  summary: string;
  start: string;
  end: string;
  htmlLink: string | null;
  linked: boolean;
}

export interface GcalExportedEvent {
  eventId: string;
  htmlLink: string | null;
  updated: string | null;
}

export interface GcalTaskLinkStatus {
  linked: boolean;
  eventId: string | null;
  calendarId: string | null;
}

export interface SyncStatus {
  state: string;
  lastSyncAt: string | null;
  pendingCount: number;
  message: string;
  lastError?: string | null;
}

export interface AiStructuredTask {
  title: string;
  description: string;
  dueDate: string | null;
  priority: "high" | "medium" | "low";
  confidence: number;
}

export interface FirstMove {
  taskId: string | null;
  message: string;
}

/** 司令塔「今日の作戦」大見出し生成に渡すタスク1件分（仕事+生活 全領域） */
export interface HeadlineTask {
  title: string;
  priority: string;
  dueDate?: string | null;
  /** 所属プロジェクト名（未分類は空文字でよい） */
  project?: string;
  /** "work" | "life"（不明は空文字でよい） */
  kind?: string;
}

/** ローカルAIが生成した「今日の作戦」大見出し（当日1回キャッシュ） */
export interface DailyHeadline {
  headline: string;
  generatedAt: string;
  /** true = 当日キャッシュをそのまま返した / false = 今回生成した */
  cached: boolean;
}

export interface ClassifyResult {
  today: string[];
  thisWeek: string[];
  later: string[];
}

export interface AiStructureContext {
  now?: string;
  existingTitles?: string[];
}

export type AgentProvider = "codex" | "claude";

/** 3段プリセット（委任UIで選ぶ）*/
export type AgentPreset = "light" | "normal" | "full";

export const PRESET_LABELS: Record<AgentPreset, string> = {
  light: "軽い",
  normal: "普通",
  full: "本気",
};

/** claude のモデルエイリアス（ホワイトリスト）*/
export const CLAUDE_MODELS = ["haiku", "sonnet", "opus"] as const;
export type ClaudeModel = (typeof CLAUDE_MODELS)[number];

/** claude effort（ホワイトリスト）*/
export const CLAUDE_EFFORTS = ["low", "medium", "high", "xhigh", "max"] as const;
export type ClaudeEffort = (typeof CLAUDE_EFFORTS)[number];

/** codex effort（ホワイトリスト）*/
export const CODEX_EFFORTS = ["none", "minimal", "low", "medium", "high", "xhigh"] as const;
export type CodexEffort = (typeof CODEX_EFFORTS)[number];

export interface AgentCommandInput {
  provider: AgentProvider;
  prompt: string;
  action?: string | null;
  missionId?: string | null;
  missionTitle?: string | null;
  preset?: AgentPreset | null;
  modelOverride?: string | null;
  effortOverride?: string | null;
}

export interface AgentCommandResult {
  provider: AgentProvider;
  output: string;
  stderr: string;
  elapsedMs: number;
  commandLabel: string;
  safetyMode: string;
}

export type AgentJobStatus = "queued" | "running" | "done" | "needs_decision" | "failed";

export interface AgentJob {
  id: string;
  missionId: string | null;
  missionTitle: string;
  provider: AgentProvider;
  prompt: string;
  action: string;
  status: AgentJobStatus;
  result: string;
  stderr: string;
  error: string;
  commandLabel: string;
  attempts: number;
  timeoutSecs: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  parentJobId: string | null;
  /** version 7: モデル・工数ヒント（null = 普通/CLIデフォルト）*/
  modelHint: string | null;
  effortHint: string | null;
}

export interface EnqueueAgentJobInput {
  provider: AgentProvider;
  prompt: string;
  action?: string | null;
  missionId?: string | null;
  missionTitle?: string | null;
  timeoutSecs?: number | null;
  parentJobId?: string | null;
  preset?: AgentPreset | null;
  modelOverride?: string | null;
  effortOverride?: string | null;
}

export interface DelegateTaskJobInput {
  taskId: string;
  provider: AgentProvider;
  preset?: AgentPreset | null;
  modelOverride?: string | null;
  effortOverride?: string | null;
}

export interface FollowupJobInput {
  parentJobId: string;
  provider: AgentProvider;
  action: "A" | "B" | "C" | "D";
  preset?: AgentPreset | null;
  modelOverride?: string | null;
  effortOverride?: string | null;
}

export interface MissionBrief {
  title: string;
  owner: string;
  action: string;
  score: number;
  dueLabel: string;
}

export interface StrategyJobInput {
  provider: AgentProvider;
  missions: MissionBrief[];
  /** ローカル日付 YYYY-MM-DD（delegationCore todayLocalKey()。UTC変換禁止） */
  todayKey: string;
  preset?: AgentPreset | null;
  modelOverride?: string | null;
  effortOverride?: string | null;
}

export interface Decision {
  id: string;
  // バックエンド(service/decisions.rs)は未紐付けを null で返す（旧IPCは "" だった）
  missionId: string | null;
  action: "A" | "B" | "C" | "D";
  reason: string;
  createdAt: string;
}

export interface AddDecisionInput {
  // "" を送っても可（バックエンドで null に正規化される）
  missionId?: string | null;
  action: "A" | "B" | "C" | "D";
  reason?: string | null;
}

export type GoalStatus = "todo" | "in_progress" | "done" | "dormant";

export interface Goal {
  id: string;
  parentId: string | null;
  title: string;
  description: string;
  dod: string;
  dueDate: string | null;
  status: GoalStatus;
  sortOrder: number;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGoalInput {
  id?: string | null;
  parentId?: string | null;
  title: string;
  description?: string | null;
  dod?: string | null;
  dueDate?: string | null;
  status?: GoalStatus | null;
  sortOrder?: number | null;
}

// 注: parentId は含めない（Rust UpdateGoalPatch に無く黙殺される。親変更は POST /goals の upsert 経由）
export interface UpdateGoalPatch {
  title?: string | null;
  description?: string | null;
  dod?: string | null;
  dueDate?: string | null;
  clearDueDate?: boolean | null;
  status?: GoalStatus | null;
  sortOrder?: number | null;
}

export type KnowledgeCategory = "prompt" | "workflow" | "mcp" | "failure" | "other";
export type KnowledgeSource = "manual" | "claude" | "mcp";

export const KNOWLEDGE_CATEGORIES: KnowledgeCategory[] = [
  "prompt",
  "workflow",
  "mcp",
  "failure",
  "other",
];

export const KNOWLEDGE_CATEGORY_LABELS: Record<KnowledgeCategory, string> = {
  prompt: "プロンプト",
  workflow: "手順",
  mcp: "ツール連携",
  failure: "失敗例",
  other: "その他",
};

export interface KnowledgeItem {
  id: string;
  title: string;
  body: string;
  /** カンマ区切り */
  tags: string;
  category: KnowledgeCategory;
  source: KnowledgeSource;
  usefulness: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKnowledgeInput {
  id?: string | null;
  title: string;
  body?: string | null;
  tags?: string | null;
  category?: KnowledgeCategory | null;
  source?: KnowledgeSource | null;
  usefulness?: number | null;
}

export interface UpdateKnowledgePatch {
  title?: string | null;
  body?: string | null;
  tags?: string | null;
  category?: KnowledgeCategory | null;
  usefulness?: number | null;
}

export interface KnowledgeFilter {
  search?: string;
  category?: KnowledgeCategory;
  tag?: string;
}

export interface FlowApi {
  listTasks: (filter: FilterMode, search?: string) => Promise<Task[]>;
  stats: () => Promise<TaskStats>;
  createTask: (input: Partial<Task> & { title: string }) => Promise<Task>;
  updateTask: (id: string, patch: Record<string, unknown>) => Promise<Task>;
  toggleTask: (id: string) => Promise<Task>;
  deleteTask: (id: string) => Promise<void>;
  restoreTask: (task: Task) => Promise<Task>;
  permanentlyDeleteTask?: (id: string) => Promise<void>;
  triggerSync: () => Promise<unknown>;
  getSyncStatus: () => Promise<SyncStatus>;
  getSettings: () => Promise<Settings>;
  updateSettings: (patch: Record<string, unknown>) => Promise<Settings>;
  testNotionConnection: (token: string, pageUrl: string) => Promise<unknown>;
  setupNotionDatabase: (token: string, pageUrl: string) => Promise<unknown>;
  aiStructureTask?: (text: string, context?: AiStructureContext) => Promise<AiStructuredTask>;
  aiClassifyTasks?: (tasks: Task[]) => Promise<ClassifyResult>;
  aiSuggestFirstMove?: (tasks: Task[]) => Promise<FirstMove>;
  aiWarmup?: () => Promise<void>;
  runAgentCommand?: (input: AgentCommandInput) => Promise<AgentCommandResult>;
  enqueueAgentJob?: (input: EnqueueAgentJobInput) => Promise<AgentJob>;
  listAgentJobs?: (limit?: number) => Promise<AgentJob[]>;
  delegateTaskJob?: (input: DelegateTaskJobInput) => Promise<AgentJob>;
  enqueueFollowupJob?: (input: FollowupJobInput) => Promise<AgentJob>;
  enqueueStrategyJob?: (input: StrategyJobInput) => Promise<AgentJob>;
  addDecision?: (input: AddDecisionInput) => Promise<Decision>;
  listDecisions?: (limit?: number) => Promise<Decision[]>;
  issueIntegrationToken?: () => Promise<string>;
  revokeIntegrationToken?: () => Promise<void>;
  getIntegrationStatus?: () => Promise<IntegrationStatus>;
  gcalStatus?: () => Promise<GcalStatus>;
  gcalConnect?: () => Promise<GcalStatus>;
  gcalDisconnect?: () => Promise<GcalStatus>;
  gcalListEvents?: (startIso: string, endIso: string) => Promise<GcalEventItem[]>;
  gcalImportEvent?: (eventId: string) => Promise<Task>;
  gcalExportTask?: (taskId: string, startIso: string, endIso: string) => Promise<GcalExportedEvent>;
  gcalTaskLinkStatus?: (taskId: string) => Promise<GcalTaskLinkStatus>;
  listProjects?: (includeArchived?: boolean) => Promise<Project[]>;
  createProject?: (input: CreateProjectInput) => Promise<Project>;
  updateProject?: (id: string, patch: UpdateProjectPatch) => Promise<Project>;
  ensureRecurringToday?: () => Promise<number>;
  kpiList?: (projectId?: string, date?: string) => Promise<KpiEntry[]>;
  kpiUpsert?: (input: UpsertKpiInput) => Promise<KpiEntry>;
  aiClassifyInbox?: (tasks: Task[], projects: ProjectBrief[]) => Promise<InboxSuggestion[]>;
  aiSuggestHeadline?: (tasks: HeadlineTask[]) => Promise<DailyHeadline>;
  listGoals?: () => Promise<Goal[]>;
  createGoal?: (input: CreateGoalInput) => Promise<Goal>;
  updateGoal?: (id: string, patch: UpdateGoalPatch) => Promise<Goal>;
  deleteGoal?: (id: string) => Promise<void>;
  listKnowledge?: (filter?: KnowledgeFilter) => Promise<KnowledgeItem[]>;
  searchKnowledge?: (filter?: KnowledgeFilter) => Promise<KnowledgeItem[]>;
  addKnowledge?: (input: CreateKnowledgeInput) => Promise<KnowledgeItem>;
  updateKnowledge?: (id: string, patch: UpdateKnowledgePatch) => Promise<KnowledgeItem>;
  deleteKnowledge?: (id: string) => Promise<void>;
  rateKnowledge?: (id: string, usefulness: number) => Promise<KnowledgeItem>;
  touchKnowledgeUsed?: (id: string) => Promise<KnowledgeItem>;
  pickDailyKnowledge?: () => Promise<KnowledgeItem | null>;
}

const tauriApi: FlowApi = {
  listTasks: (filter, search) => invoke<Task[]>("list_tasks", { filter, search }),
  stats: () => invoke<TaskStats>("task_stats"),
  createTask: (input) => invoke<Task>("create_task", { input }),
  updateTask: (id, patch) => invoke<Task>("update_task", { id, patch }),
  toggleTask: (id) => invoke<Task>("toggle_task", { id }),
  deleteTask: (id) => invoke<void>("delete_task", { id }),
  restoreTask: (task) => invoke<Task>("restore_task", { task }),
  triggerSync: () => invoke("trigger_sync"),
  getSyncStatus: () => invoke<SyncStatus>("get_sync_status"),
  getSettings: () => invoke<Settings>("get_settings"),
  updateSettings: (patch) => invoke<Settings>("update_settings", { patch }),
  testNotionConnection: (token, pageUrl) =>
    invoke("test_notion_connection", { token, pageUrl }),
  setupNotionDatabase: (token, pageUrl) =>
    invoke("setup_notion_database", { token, pageUrl }),
  aiStructureTask: (text, context) =>
    invoke<AiStructuredTask>("ai_structure_task", { text, context }),
  aiClassifyTasks: (tasks) =>
    invoke<ClassifyResult>("ai_classify_tasks", { tasks }),
  aiSuggestFirstMove: (tasks) =>
    invoke<FirstMove>("ai_suggest_first_move", { tasks }),
  aiWarmup: () => invoke<void>("ai_warmup"),
  runAgentCommand: (input) =>
    invoke<AgentCommandResult>("run_agent_command", { input }),
  enqueueAgentJob: (input) =>
    invoke<AgentJob>("enqueue_agent_job", { input }),
  listAgentJobs: (limit) =>
    invoke<AgentJob[]>("list_agent_jobs", { limit }),
  delegateTaskJob: (input) => invoke<AgentJob>("delegate_task_job", { input }),
  enqueueFollowupJob: (input) => invoke<AgentJob>("enqueue_followup_job", { input }),
  enqueueStrategyJob: (input) => invoke<AgentJob>("enqueue_strategy_job", { input }),
  addDecision: (input) =>
    invoke<Decision>("add_decision", { input }),
  listDecisions: (limit) =>
    invoke<Decision[]>("list_decisions", { limit }),
  issueIntegrationToken: () => invoke<string>("issue_integration_token"),
  revokeIntegrationToken: () => invoke<void>("revoke_integration_token"),
  getIntegrationStatus: () =>
    invoke<IntegrationStatus>("integration_status"),
  gcalStatus: () => invoke<GcalStatus>("gcal_status"),
  gcalConnect: () => invoke<GcalStatus>("gcal_connect"),
  gcalDisconnect: () => invoke<GcalStatus>("gcal_disconnect"),
  gcalListEvents: (startIso, endIso) =>
    invoke<GcalEventItem[]>("gcal_list_events", { startIso, endIso }),
  gcalImportEvent: (eventId) =>
    invoke<Task>("gcal_import_event", { eventId }),
  gcalExportTask: (taskId, startIso, endIso) =>
    invoke<GcalExportedEvent>("gcal_export_task", { taskId, startIso, endIso }),
  gcalTaskLinkStatus: (taskId) =>
    invoke<GcalTaskLinkStatus>("gcal_task_link_status", { taskId }),
  listProjects: (includeArchived) =>
    invoke<Project[]>("list_projects", { includeArchived }),
  createProject: (input) => invoke<Project>("create_project", { input }),
  updateProject: (id, patch) => invoke<Project>("update_project", { id, patch }),
  ensureRecurringToday: () => invoke<number>("ensure_recurring_today"),
  kpiList: (projectId, date) => invoke<KpiEntry[]>("kpi_list", { projectId, date }),
  kpiUpsert: (input) => invoke<KpiEntry>("kpi_upsert", { input }),
  aiClassifyInbox: (tasks, projects) =>
    invoke<InboxSuggestion[]>("ai_classify_inbox", { tasks, projects }),
  aiSuggestHeadline: (tasks) =>
    invoke<DailyHeadline>("ai_suggest_headline", { tasks }),
  listGoals: () => invoke<Goal[]>("list_goals"),
  createGoal: (input) => invoke<Goal>("create_goal", { input }),
  updateGoal: (id, patch) => invoke<Goal>("update_goal", { id, patch }),
  deleteGoal: (id) => invoke<void>("delete_goal", { id }),
  listKnowledge: (filter) =>
    invoke<KnowledgeItem[]>("list_knowledge", {
      search: filter?.search,
      category: filter?.category,
      tag: filter?.tag,
    }),
  searchKnowledge: (filter) =>
    invoke<KnowledgeItem[]>("search_knowledge", {
      search: filter?.search,
      category: filter?.category,
      tag: filter?.tag,
    }),
  addKnowledge: (input) => invoke<KnowledgeItem>("add_knowledge", { input }),
  updateKnowledge: (id, patch) => invoke<KnowledgeItem>("update_knowledge", { id, patch }),
  deleteKnowledge: (id) => invoke<void>("delete_knowledge", { id }),
  rateKnowledge: (id, usefulness) =>
    invoke<KnowledgeItem>("rate_knowledge", { id, usefulness }),
  touchKnowledgeUsed: (id) => invoke<KnowledgeItem>("touch_knowledge_used", { id }),
  pickDailyKnowledge: () => invoke<KnowledgeItem | null>("pick_daily_knowledge"),
};

export const isTauriEnvironment = (): boolean => {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  return (
    "__TAURI_INTERNALS__" in w ||
    "__TAURI_METADATA__" in w ||
    "__TAURI__" in w
  );
};

let resolvedApi: FlowApi = tauriApi;
let readyPromise: Promise<void> = Promise.resolve();

if (!isTauriEnvironment()) {
  readyPromise = import("./api-pwa")
    .then((m) => {
      resolvedApi = m.api as FlowApi;
    })
    .catch((err) => {
      console.error("[Flow] Failed to load PWA api, falling back to tauri", err);
    });
}

export const api: FlowApi = new Proxy({} as FlowApi, {
  get(_target, prop: string | symbol) {
    return (...args: unknown[]) =>
      readyPromise.then(() => {
        const fn = (resolvedApi as unknown as Record<string, (...a: unknown[]) => unknown>)[
          prop as string
        ];
        if (typeof fn !== "function") {
          throw new Error(`Flow api method not found: ${String(prop)}`);
        }
        return fn(...args);
      });
  },
});
